import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import LiveCard from "@/components/base/LiveCard";
import { liveCategories, liveSessions, type LiveStatus } from "@/mocks/lives";
import { cn, formatViewers } from "@/utils/format";
import { SITE } from "@/config/site";

type StatusFilter = "all" | LiveStatus;
type SortKey = "hot" | "new" | "signal";

const statusOptions: { key: StatusFilter; label: string; icon: string }[] = [
  { key: "all", label: "全部状态", icon: "ri-list-check-2" },
  { key: "live", label: "正在直播", icon: "ri-live-line" },
  { key: "upcoming", label: "即将开播", icon: "ri-time-line" },
  { key: "replay", label: "回看中", icon: "ri-history-line" },
];

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "hot", label: "按观看人数" },
  { key: "new", label: "按开播时间" },
  { key: "signal", label: "按信号强度" },
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "直播大厅 - 爆料直播现场信号总览",
    url: `${SITE.url}/live`,
    inLanguage: "zh-CN",
    description:
      "直播大厅汇集爆料直播全部现场信号，可按类型、状态筛选并搜索关键词，查看现场实时画面与连麦声音。",
    isPartOf: { "@type": "WebSite", url: SITE.url },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: `${SITE.url}/` },
      { "@type": "ListItem", position: 2, name: "直播大厅", item: `${SITE.url}/live` },
    ],
  },
];

export default function LiveList() {
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") ?? "全部现场";

  const [keyword, setKeyword] = useState(searchParams.get("q") ?? "");
  const [category, setCategory] = useState(initialCategory);
  const [status, setStatus] = useState<StatusFilter>("all");
  const [sort, setSort] = useState<SortKey>("hot");
  const [visible, setVisible] = useState(6);

  const filtered = useMemo(() => {
    const list = liveSessions.filter((item) => {
      const matchCategory = category === "全部现场" || item.category === category;
      const matchStatus = status === "all" || item.status === status;
      const text = `${item.title}${item.summary}${item.location}${item.host}${item.tags.join("")}`;
      const matchKeyword = keyword.trim() === "" || text.includes(keyword.trim());
      return matchCategory && matchStatus && matchKeyword;
    });

    return [...list].sort((a, b) => {
      if (sort === "signal") return b.signal - a.signal;
      if (sort === "new") return b.viewers - a.viewers;
      return b.viewers - a.viewers;
    });
  }, [category, status, keyword, sort]);

  const shown = filtered.slice(0, visible);
  const liveCount = liveSessions.filter((item) => item.status === "live").length;

  return (
    <>
      <Seo
        title="直播大厅｜现场实时画面与信号总览 - 爆料直播"
        description="爆料直播大厅汇集全部现场信号，支持按突发现场、民生追踪、城市路况等类型筛选，也能按直播状态与关键词搜索。每场直播都标注实时画面信号强度，点开即可看到现场镜头与连麦声音。"
        keywords="爆料直播大厅,现场直播列表,实时画面信号,直播镜头追踪,突发事态直击"
        path="/live"
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden border-b border-background-200/70 pt-16 md:pt-20">
        <div className="stage-grid absolute inset-0 opacity-60" aria-hidden="true" />
        <div className="relative w-full px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "直播大厅" },
            ]}
          />
          <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="inline-flex items-center gap-2 rounded-full border border-accent-400/40 bg-accent-500/10 px-3 py-1.5 font-label text-[11px] tracking-wider text-accent-700">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent-500" aria-hidden="true" />
                {liveCount} 场正在直播 · {SITE.onlineSignals}
              </p>
              <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
                直播大厅 · 现场信号总览
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                所有正在回传的现场实时画面都在这里。按类型筛选、按状态排查，或者直接搜索地点与关键词，
                快速锁定你想看的那一路直播镜头。
              </p>
            </div>

            <div className="w-full lg:max-w-sm">
              <label htmlFor="live-search" className="mb-2 block font-label text-[11px] uppercase tracking-widest text-foreground-500">
                搜索现场
              </label>
              <div className="flex items-center gap-2 rounded-md border border-background-300/60 bg-background-100 px-3 py-2.5 focus-within:border-primary-500/70">
                <i className="ri-search-line text-base text-foreground-500" aria-hidden="true" />
                <input
                  id="live-search"
                  type="search"
                  value={keyword}
                  onChange={(event) => {
                    setKeyword(event.target.value);
                    setVisible(6);
                  }}
                  placeholder="输入地点、事件或主播名"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
                {keyword && (
                  <button
                    type="button"
                    aria-label="清空搜索关键词"
                    onClick={() => setKeyword("")}
                    className="cursor-pointer text-foreground-500 transition-colors hover:text-primary-500"
                  >
                    <i className="ri-close-circle-line" aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 px-4 py-8 md:px-6 md:py-12">
        <div className="flex flex-col gap-5 border-b border-background-200/70 pb-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 font-label text-[11px] uppercase tracking-widest text-foreground-500">
              现场类型
            </span>
            {["全部现场", ...liveCategories.map((cat) => cat.name)].map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => {
                  setCategory(name);
                  setVisible(6);
                }}
                aria-pressed={category === name}
                className={cn(
                  "cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] transition-colors",
                  category === name
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300/60 text-foreground-700 hover:border-primary-500/60 hover:text-primary-500"
                )}
              >
                {name}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              {statusOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    setStatus(option.key);
                    setVisible(6);
                  }}
                  aria-pressed={status === option.key}
                  className={cn(
                    "inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-[13px] transition-colors",
                    status === option.key
                      ? "bg-secondary-100/80 text-secondary-900"
                      : "text-foreground-600 hover:text-foreground-950"
                  )}
                >
                  <i className={option.icon} aria-hidden="true" />
                  {option.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <span className="font-label text-[12px] text-foreground-500">
                共 {filtered.length} 路信号
              </span>
              <label htmlFor="live-sort" className="sr-only">
                排序方式
              </label>
              <select
                id="live-sort"
                value={sort}
                onChange={(event) => setSort(event.target.value as SortKey)}
                className="cursor-pointer rounded-md border border-background-300/60 bg-background-100 px-3 py-1.5 text-[13px] text-foreground-800 outline-none transition-colors hover:border-primary-500/60 focus:border-primary-500/70"
              >
                {sortOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {shown.length > 0 ? (
          <>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
              {shown.map((item, index) => (
                <LiveCard key={item.id} item={item} index={index + 1} />
              ))}
            </div>

            <div className="mt-10 flex flex-col items-center gap-4">
              <p className="text-xs text-foreground-500">
                已展示 {shown.length} / {filtered.length} 路信号 · 累计{" "}
                {formatViewers(filtered.reduce((sum, item) => sum + item.viewers, 0))} 人在看
              </p>
              {visible < filtered.length ? (
                <button
                  type="button"
                  onClick={() => setVisible((count) => count + 6)}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 px-6 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-500/60 hover:text-primary-500"
                >
                  <i className="ri-arrow-down-double-line" aria-hidden="true" />
                  加载更多现场信号
                </button>
              ) : (
                <span className="inline-flex items-center gap-2 text-xs text-foreground-400">
                  <i className="ri-checkbox-circle-line" aria-hidden="true" />
                  已经到底了，编辑组正在核实新的现场线索
                </span>
              )}
            </div>
          </>
        ) : (
          <div className="mt-12 flex flex-col items-center gap-4 rounded-lg border border-dashed border-background-300/70 bg-background-100/50 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200/70 text-foreground-500">
              <i className="ri-search-eye-line text-2xl" aria-hidden="true" />
            </span>
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              没有找到匹配的现场信号
            </h2>
            <p className="max-w-md text-[13px] leading-relaxed text-foreground-600">
              换个关键词试试，或者把类型与状态筛选恢复为全部。如果你的线索确实存在，
              也可以通过投稿邮箱告诉编辑组。
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setCategory("全部现场");
                  setStatus("all");
                  setVisible(6);
                }}
                className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line" aria-hidden="true" />
                重置全部筛选
              </button>
              <a
                href={`mailto:${SITE.tipEmail}`}
                className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/60 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-500/60 hover:text-primary-500"
              >
                <i className="ri-send-plane-line" aria-hidden="true" />
                提供现场线索
              </a>
            </div>
          </div>
        )}
      </section>
    </>
  );
}